import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import { useGetInvitationQuery } from '@/state/queries/invitations/use-get-invitation.ts';
import { useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import NewEmailVerification from './new-email-verification';
import OrganizationInvitationGate from './organization-invitation-gate';
import { ServerError } from '@/types/errors/server.error';
import { Typography } from '@mui/material';

export default function InvitedToEntityWithSearchParam() {
  const [searchParams] = useSearchParams();
  const invitationKey = searchParams.get('invitationKey');
  const email = searchParams.get('email');
  const contributorId = searchParams.get('contributorId');
  const type = searchParams.get('type');

  if (invitationKey) {
    return <UnifiedInvitationModal invitationKey={invitationKey} type="organization" />;
  }

  if (email && type === 'contributor') {
    return <UnifiedInvitationModal email={email} contributorId={contributorId ?? undefined} type="contributor" />;
  }

  return null;
}

interface UnifiedInvitationModalProps {
  invitationKey?: string;
  email?: string;
  contributorId?: string;
  type: 'organization' | 'contributor';
}

function UnifiedInvitationModal({ invitationKey, email, contributorId, type }: UnifiedInvitationModalProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { user } = useUser();

  // Always call the hook to satisfy React Hooks rules
  const organizationQuery = useGetInvitationQuery(invitationKey || '');
  const { data: invitation, error: invitationError } = invitationKey ? organizationQuery : { data: null, error: null };

  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (type === 'contributor') {
      const isNewEmail =
        email &&
        user?.emailAddresses
          .filter((e) => e.verification.status === 'verified')
          .some((verifiedEmail) => verifiedEmail.emailAddress === email) === false;

      if (isNewEmail) {
        setOpen(true);
      }
    } else if (invitation) {
      setOpen(true);
    }
  }, [invitation, user, email, type]);

  useEffect(() => {
    if (!invitationError) return;

    const error = invitationError as ServerError;
    if (error.statusCode === 404) {
      setErrorMessage(t('invitationFlow.notFound'));
    } else if (error.errorCode === 'INVITED_EMAIL_TAKEN') {
      setErrorMessage(t('invitationFlow.emailTakenError'));
    } else if (error.errorCode === 'INVITATION_EXPIRED') {
      setErrorMessage(t('invitationFlow.invitationExpiredError'));
    }
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationError]);

  const handleClose = () => {
    setOpen(false);
    setErrorMessage(null);
    if (type === 'contributor' && contributorId) {
      navigate(`/contributions/${contributorId}`);
      return;
    }
    navigate('/');
  };

  const getModalTitle = () => {
    if (errorMessage) {
      return t('invitationFlow.invitationError');
    }

    if (type === 'contributor') {
      return t('invitationFlow.contributorInvitationTitle') || 'Contributor Invitation';
    }

    return t('invitationFlow.title');
  };

  return (
    <Modal open={open} handleClose={handleClose} title={getModalTitle()}>
      {errorMessage ? (
        <Typography variant="body2" color="text.secondary">
          {errorMessage}
        </Typography>
      ) : type === 'contributor' ? (
        <NewEmailVerification email={email!} contributorId={contributorId} onSuccess={handleClose} />
      ) : invitation && invitationKey ? (
        <OrganizationInvitationGate invitation={invitation} invitationKey={invitationKey} onSuccess={handleClose} />
      ) : null}
    </Modal>
  );
}
